import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Radio,
  Checkbox,
  Grid,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Restore as RestoreIcon,
  PlayArrow as PlayIcon,
  Image as ImageIcon,
  DragIndicator as DragIcon,
  AutoFixHigh as AutoIcon,
  NewReleases as NewIcon,
  Laptop as LaptopIcon,
  Tablet as TabletIcon,
  Smartphone as PhoneIcon,
  ArrowBack as BackIcon,
  CloudSync as CloudSyncIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api, OnboardingSurveyConfig, OnboardingQuestion, QuestionOption, OnboardingHistoryEntry, AssetVersion, OnboardingScreen, StagingFile } from '../services/api-client';
import { useAuth } from '../contexts/GoogleAuthContext';

// =============================================================================
// Types & Helpers
// =============================================================================

interface UndoState {
  config: OnboardingSurveyConfig;
  timestamp: number;
}

const toSnakeCase = (text: string): string => {
  if (!text) return '';
  return text.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
};

const collectKnownSfValues = (config: OnboardingSurveyConfig | null): Set<string> => {
  const values = new Set<string>();
  if (!config?.questions) return values;
  config.questions.forEach(q => q.options?.forEach(o => { if (o.salesforce_value) values.add(o.salesforce_value); }));
  return values;
};

const extractFirstFrame = async (videoUrl: string): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = videoUrl;
    video.currentTime = 0.5;
    video.muted = true;
    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0);
          canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Failed')), 'image/jpeg', 0.85);
        } else reject(new Error('No context'));
      } catch (e) { reject(e); }
    });
    video.addEventListener('error', () => reject(new Error('Load error')));
    video.load();
  });
};

const formatDate = (d: string) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

const parseQuestionText = (text: string, highlight?: string | null) => {
  if (highlight && text.includes(highlight)) {
    const parts = text.split(highlight);
    return (
      <>
        {parts[0]}
        <span style={{ fontFamily: '"Reckless Neue", Georgia, serif', fontStyle: 'italic', fontWeight: 300 }}>
          {highlight}
        </span>
        {parts.slice(1).join(highlight)}
      </>
    );
  }
  return text;
};

// =============================================================================
// Survey Preview Component (Responsive)
// =============================================================================

interface SurveyPreviewProps {
  question: OnboardingQuestion;
  currentStep: number;
  totalSteps: number;
}

function SurveyPreview({ question, currentStep, totalSteps }: SurveyPreviewProps) {
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [selected, setSelected] = useState<string[]>([]);
  const [scale, setScale] = useState(0.45);
  const isCheckbox = question.input_type === 'checkbox';

  useEffect(() => setSelected([]), [question.id]);

  const handleSelect = (val: string) => {
    if (isCheckbox) setSelected(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
    else setSelected([val]);
  };

  const getContainerDimensions = () => {
    switch (device) {
      case 'mobile': return { width: 390, height: 844 };
      case 'tablet': return { width: 834, height: 1112 };
      default: return { width: 1440, height: 900 };
    }
  };

  const dims = getContainerDimensions();
  const showRightPanel = device !== 'mobile';

  // TripsbyAppUpdated exact colors
  const colors = {
    pageBg: '#FFFFFF',
    cardBg: '#EDEFF5',
    cardSelected: '#F2F7F4',
    borderSelected: '#4D5F53',
    accent: '#569469',
    text: '#09090A',
    textSecondary: '#676970',
    testimonialNick: '#A7D1AA',
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', maxHeight: 520 }}>
      {/* Device Toggle Header */}
      <Box sx={{ p: 1, borderBottom: '1px solid #ddd', bgcolor: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, ml: 1, whiteSpace: 'nowrap' }}>Preview</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">{Math.round(scale * 100)}%</Typography>
          <input type="range" min="0.25" max="0.8" step="0.05" value={scale} onChange={(e) => setScale(parseFloat(e.target.value))} style={{ flex: 1, height: 4, cursor: 'pointer' }} />
        </Box>
        <ToggleButtonGroup value={device} exclusive onChange={(_, v) => v && setDevice(v)} size="small" sx={{ height: 24 }}>
          <ToggleButton value="desktop" sx={{ py: 0, px: 0.5 }}><LaptopIcon sx={{ fontSize: 14 }} /></ToggleButton>
          <ToggleButton value="tablet" sx={{ py: 0, px: 0.5 }}><TabletIcon sx={{ fontSize: 14 }} /></ToggleButton>
          <ToggleButton value="mobile" sx={{ py: 0, px: 0.5 }}><PhoneIcon sx={{ fontSize: 14 }} /></ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Preview Viewport */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', p: 1, bgcolor: '#c8c8c8' }}>
        {/* TripsbyAppUpdated OnboardingLayout - Full page layout */}
        <Box
          sx={{
            width: dims.width,
            height: dims.height,
            bgcolor: colors.pageBg,
            display: 'flex',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
            transition: 'transform 0.2s',
            flexShrink: 0,
            p: device === 'mobile' ? '20px' : '35px 28px 26px 140px',
          }}
        >
          {/* Left Panel - Logo & Step Counter */}
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: device === 'mobile' ? 0 : 120, pr: device === 'mobile' ? 0 : 3 }}>
            {/* Logo */}
            <Box sx={{ p: '8px 12px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 28, height: 28, border: '2px solid #000', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>✈</Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: -0.5 }}>TripsByAI</Typography>
              </Box>
            </Box>
            {/* Step Counter */}
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.text }}>
              Step {currentStep}/{totalSteps}
            </Typography>
          </Box>

          {/* Center Content - Form */}
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 500, mx: 'auto' }}>
            {/* Back Button */}
            <Button size="small" startIcon={<BackIcon />} sx={{ color: '#000', mb: 2, pl: 0, textTransform: 'none', alignSelf: 'flex-start', fontWeight: 500 }}>Back</Button>

            {/* Question Text - Matches onboardingForm.module.scss */}
            <Typography sx={{
              color: '#000',
              fontSize: device === 'mobile' ? 28 : 36,
              fontWeight: 700,
              lineHeight: '40px',
              letterSpacing: '-0.432px',
              mb: 1.5,
            }}>
              {parseQuestionText(question.question_text || '', question.question_text_highlight)}
            </Typography>

            {/* Options - Matches onboardingInputs.module.scss .card */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
              {question.options?.map((opt) => {
                const isSel = selected.includes(opt.firebase_value || opt.id);
                return (
                  <Box
                    key={opt.id}
                    onClick={() => handleSelect(opt.firebase_value || opt.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      px: '2px',
                      height: 56,
                      bgcolor: isSel ? colors.cardSelected : colors.cardBg,
                      border: `1px solid ${isSel ? colors.borderSelected : colors.cardBg}`,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': { borderColor: isSel ? colors.borderSelected : '#d1d4e0' }
                    }}
                  >
                    {isCheckbox ? (
                      <Checkbox checked={isSel} size="small" sx={{ color: colors.accent, '&.Mui-checked': { color: colors.accent } }} />
                    ) : (
                      <Radio checked={isSel} size="small" sx={{ color: colors.accent, '&.Mui-checked': { color: colors.accent } }} />
                    )}
                    <Typography sx={{ fontWeight: 400, fontSize: 16, color: colors.text, ml: 0.5 }}>
                      {opt.ui_label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Next Button - Matches .nextButton */}
            <Button
              variant="contained"
              disabled={selected.length === 0}
              sx={{
                mt: 4,
                width: 171,
                height: 60,
                bgcolor: colors.accent,
                color: '#fff',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 16,
                '&:hover': { bgcolor: '#4a8059' },
                '&.Mui-disabled': { bgcolor: colors.cardBg, color: '#B1B2BA' }
              }}
            >
              Next
            </Button>
          </Box>

          {/* Right Panel - Media with Overlay (Tablet/Desktop only) */}
          {showRightPanel && (
            <Box sx={{
              flex: 1,
              position: 'relative',
              borderRadius: '42px',
              overflow: 'hidden',
              ml: 3,
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0, left: 0, width: '100%', height: '100%',
                background: 'linear-gradient(180deg, rgba(0,0,0,0) 86.59%, rgba(0,0,0,0.55) 93.65%)',
                zIndex: 1,
                pointerEvents: 'none',
              }
            }}>
              {/* Video/Image */}
              {question.page_content?.media_url ? (
                question.page_content.media_type === 'video' ? (
                  <video
                    src={question.page_content.media_url}
                    poster={question.page_content.media_poster_url || undefined}
                    autoPlay loop muted playsInline
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Box component="img" src={question.page_content.media_url} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )
              ) : (
                <Box sx={{
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>No media configured</Typography>
                </Box>
              )}

              {/* Testimonial Overlay - Matches .rightContent */}
              {question.page_content?.overlay && (question.page_content.overlay.text || question.page_content.overlay.attribution) && (
                <Box sx={{
                  position: 'absolute',
                  bottom: 20,
                  left: 48,
                  right: 48,
                  display: 'inline-flex',
                  p: '8px 24px 8px 8px',
                  alignItems: 'center',
                  gap: 1.5,
                  borderRadius: '35px',
                  bgcolor: 'rgba(0,0,0,0.51)',
                  backdropFilter: 'blur(5px)',
                  zIndex: 3,
                }}>
                  {question.page_content.overlay.avatar_url && (
                    <Box component="img" src={question.page_content.overlay.avatar_url} sx={{ width: 44, height: 44, borderRadius: '24px' }} />
                  )}
                  <Box>
                    <Typography sx={{ color: '#FFF', fontSize: 16, fontWeight: 400, lineHeight: '20px' }}>
                      {question.page_content.overlay.text}
                    </Typography>
                    <Typography sx={{ color: colors.testimonialNick, fontSize: 12, fontWeight: 500, lineHeight: '16px' }}>
                      {question.page_content.overlay.attribution}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
}

// =============================================================================
// Sortable Option Row (Compact)
// =============================================================================

interface SortableOptionProps {
  option: QuestionOption;
  index: number;
  onUpdate: (idx: number, field: string, value: string) => void;
  onRemove: (idx: number) => void;
  onAutoGen: (idx: number) => void;
  disabled: boolean;
  isNewSfValue: boolean;
}

function SortableOption({ option, index, onUpdate, onRemove, onAutoGen, disabled, isNewSfValue }: SortableOptionProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: option.id || `opt-${index}`
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasSpaces = option.firebase_value?.includes(' ');

  return (
    <TableRow ref={setNodeRef} style={style} sx={{ bgcolor: isDragging ? '#f5f5f5' : 'inherit' }}>
      <TableCell sx={{ p: 0.5, width: 32 }}>
        <IconButton {...attributes} {...listeners} size="small" disabled={disabled} sx={{ cursor: disabled ? 'default' : 'grab' }}>
          <DragIcon fontSize="small" />
        </IconButton>
      </TableCell>
      <TableCell sx={{ p: 0.5 }}>
        <TextField
          size="small"
          value={option.id || ''}
          onChange={(e) => onUpdate(index, 'id', e.target.value)}
          disabled={disabled}
          variant="standard"
          fullWidth
          InputProps={{ disableUnderline: true, sx: { fontSize: 13 } }}
        />
      </TableCell>
      <TableCell sx={{ p: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            size="small"
            value={option.ui_label || ''}
            onChange={(e) => onUpdate(index, 'ui_label', e.target.value)}
            disabled={disabled}
            variant="standard"
            fullWidth
            InputProps={{ disableUnderline: true, sx: { fontSize: 13 } }}
          />
          {option.ui_label && !disabled && (
            <Tooltip title="Auto-generate frontend value">
              <IconButton size="small" onClick={() => onAutoGen(index)} sx={{ p: 0.25 }}>
                <AutoIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ p: 0.5 }}>
        <TextField
          size="small"
          value={option.firebase_value || ''}
          onChange={(e) => onUpdate(index, 'firebase_value', e.target.value)}
          disabled={disabled}
          variant="standard"
          fullWidth
          error={!!hasSpaces}
          InputProps={{ disableUnderline: true, sx: { fontSize: 13, color: hasSpaces ? 'error.main' : 'inherit' } }}
        />
      </TableCell>
      <TableCell sx={{ p: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            size="small"
            value={option.salesforce_value || ''}
            onChange={(e) => onUpdate(index, 'salesforce_value', e.target.value)}
            disabled={disabled}
            variant="standard"
            fullWidth
            InputProps={{ disableUnderline: true, sx: { fontSize: 13 } }}
          />
          {isNewSfValue && option.salesforce_value && (
            <Tooltip title="New SF value - add to picklist!">
              <NewIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ p: 0.5, width: 32 }}>
        <IconButton size="small" onClick={() => onRemove(index)} disabled={disabled} color="error" sx={{ p: 0.25 }}>
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

// =============================================================================
// Question Editor (Compact Accordion)
// =============================================================================

// Screen Editor for Start/End screens
interface ScreenEditorProps {
  title: string;
  screen: OnboardingScreen | undefined;
  onChange: (screen: OnboardingScreen) => void;
  onUploadVideo: () => void;
  onUploadPoster: () => void;
  onUploadAvatar: () => void;
  onViewVersions: (assetPath: string) => void;
  onClearVideo: () => void;
  isUploading: boolean;
  isDeveloper: boolean;
}

function ScreenEditor({
  title, screen, onChange, onUploadVideo, onUploadPoster, onUploadAvatar, onViewVersions, onClearVideo, isUploading, isDeveloper
}: ScreenEditorProps) {
  const defaultScreen: OnboardingScreen = {
    enabled: false,
    title: '',
    title_highlight: '',
    subtitle: '',
    button_text: 'Continue',
    page_content: {
      media_type: 'video',
      media_url: null,
      media_poster_url: null,
      overlay: { text: '', attribution: '', avatar_url: '' },
    },
  };

  const currentScreen = screen || defaultScreen;

  const updateField = (field: keyof OnboardingScreen, value: any) => {
    onChange({ ...currentScreen, [field]: value });
  };

  const updateOverlay = (field: string, value: string) => {
    const defaultOverlay = { text: '', attribution: '', avatar_url: null };
    onChange({
      ...currentScreen,
      page_content: {
        ...currentScreen.page_content!,
        overlay: { ...defaultOverlay, ...currentScreen.page_content?.overlay, [field]: value },
      },
    });
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={currentScreen.enabled}
                onChange={(e) => updateField('enabled', e.target.checked)}
                disabled={!isDeveloper}
                size="small"
              />
            }
            label={currentScreen.enabled ? 'Enabled' : 'Disabled'}
            labelPlacement="start"
          />
        </Box>

        {currentScreen.enabled && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                size="small"
                label="Screen Title"
                value={currentScreen.title || ''}
                onChange={(e) => updateField('title', e.target.value)}
                disabled={!isDeveloper}
                sx={{ flex: 2 }}
              />
              <TextField
                size="small"
                label="Highlight (italic)"
                value={currentScreen.title_highlight || ''}
                onChange={(e) => updateField('title_highlight', e.target.value)}
                disabled={!isDeveloper}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Button Text"
                value={currentScreen.button_text || 'Continue'}
                onChange={(e) => updateField('button_text', e.target.value)}
                disabled={!isDeveloper}
                sx={{ width: 150 }}
              />
            </Box>

            <TextField
              size="small"
              label="Subtitle"
              value={currentScreen.subtitle || ''}
              onChange={(e) => updateField('subtitle', e.target.value)}
              disabled={!isDeveloper}
              multiline
              rows={2}
            />

            {/* Video Section */}
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                Video & Overlay
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Box sx={{ width: 120, flexShrink: 0 }}>
                  {currentScreen.page_content?.media_url ? (
                    <video
                      src={currentScreen.page_content.media_url}
                      poster={currentScreen.page_content.media_poster_url || undefined}
                      style={{ width: '100%', height: 68, objectFit: 'cover', borderRadius: 4 }}
                    />
                  ) : (
                    <Box sx={{ width: '100%', height: 68, bgcolor: '#e0e0e0', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PlayIcon sx={{ opacity: 0.4 }} />
                    </Box>
                  )}
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
                    onClick={onUploadVideo}
                    disabled={!isDeveloper || isUploading}
                    sx={{ fontSize: 11, p: 0.25, mt: 0.5, width: '100%' }}
                  >
                    Video
                  </Button>
                </Box>

                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      label="Video URL"
                      value={currentScreen.page_content?.media_url || ''}
                      disabled
                      sx={{ flex: 1 }}
                      InputProps={{ sx: { fontSize: 12 } }}
                    />
                    {currentScreen.page_content?.media_url && (
                      <>
                        <Tooltip title="View version history">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const url = currentScreen.page_content?.media_url || '';
                              const path = url.replace('https://storage.googleapis.com/tripsby_public_hosted_content/', '');
                              onViewVersions(path);
                            }}
                          >
                            <HistoryIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove video">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={onClearVideo}
                            disabled={!isDeveloper}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title="Upload poster">
                      <IconButton size="small" onClick={onUploadPoster} disabled={!isDeveloper || isUploading}>
                        <ImageIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <TextField
                      size="small"
                      label="Overlay Text"
                      value={currentScreen.page_content?.overlay?.text || ''}
                      onChange={(e) => updateOverlay('text', e.target.value)}
                      disabled={!isDeveloper}
                      sx={{ flex: 2 }}
                      InputProps={{ sx: { fontSize: 12 } }}
                    />
                    <TextField
                      size="small"
                      label="Attribution"
                      value={currentScreen.page_content?.overlay?.attribution || ''}
                      onChange={(e) => updateOverlay('attribution', e.target.value)}
                      disabled={!isDeveloper}
                      sx={{ flex: 1 }}
                      InputProps={{ sx: { fontSize: 12 } }}
                      placeholder="@user"
                    />
                    <Tooltip title="Upload avatar">
                      <IconButton size="small" onClick={onUploadAvatar} disabled={!isDeveloper || isUploading}>
                        <UploadIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

interface QuestionEditorProps {
  question: OnboardingQuestion;
  onChange: (updated: OnboardingQuestion) => void;
  onDelete: (order: number) => void;
  onUploadVideo: (order: number) => void;
  onUploadPoster: (order: number) => void;
  onUploadAvatar: (order: number) => void;
  onViewVersions: (assetPath: string, assetType: 'video' | 'poster' | 'avatar') => void;
  onClearVideo: (order: number) => void;
  isUploading: boolean;
  isDeveloper: boolean;
  hasUnsavedChanges: boolean;
  knownSfValues: Set<string>;
  expanded: boolean;
  onExpand: (isExpanded: boolean) => void;
}

function QuestionEditor({
  question, onChange, onDelete, onUploadVideo, onUploadPoster, onUploadAvatar, onViewVersions, onClearVideo,
  isUploading, isDeveloper, hasUnsavedChanges, knownSfValues, expanded, onExpand
}: QuestionEditorProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const updateField = (field: string, value: any) => onChange({ ...question, [field]: value });
  const updateOverlay = (field: string, value: any) => onChange({
    ...question,
    page_content: {
      ...question.page_content,
      overlay: { ...question.page_content?.overlay, [field]: value },
    },
  });

  const updateOption = (idx: number, field: string, value: string) => {
    const opts = [...(question.options || [])];
    opts[idx] = { ...opts[idx], [field]: value };
    onChange({ ...question, options: opts });
  };

  const autoGenFirebaseValue = (idx: number) => {
    const opt = question.options?.[idx];
    if (opt?.ui_label) {
      const opts = [...(question.options || [])];
      opts[idx] = { ...opts[idx], firebase_value: toSnakeCase(opt.ui_label) };
      onChange({ ...question, options: opts });
    }
  };

  const addOption = () => {
    const opts = [...(question.options || [])];
    opts.push({ id: `opt_${Date.now()}`, ui_label: '', firebase_value: '', salesforce_value: '' });
    onChange({ ...question, options: opts });
  };

  const removeOption = (idx: number) => {
    const opts = [...(question.options || [])];
    opts.splice(idx, 1);
    onChange({ ...question, options: opts });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const opts = question.options || [];
      const oldIdx = opts.findIndex((o, i) => (o.id || `opt-${i}`) === active.id);
      const newIdx = opts.findIndex((o, i) => (o.id || `opt-${i}`) === over.id);
      if (oldIdx !== -1 && newIdx !== -1) {
        onChange({ ...question, options: arrayMove(opts, oldIdx, newIdx) });
      }
    }
  };

  const isNewSfValue = (val: string): boolean => Boolean(val && !knownSfValues.has(val));

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExp) => onExpand(isExp)}
      sx={{
        mb: 1,
        border: hasUnsavedChanges ? '2px solid #ff9800' : '1px solid #e0e0e0',
        '&:before': { display: 'none' },
        boxShadow: 'none',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 40,
          '&.Mui-expanded': { minHeight: 40 },
          backgroundColor: hasUnsavedChanges ? '#fff8e1' : '#fafafa',
          '& .MuiAccordionSummary-content': { my: 0.5 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Chip label={`Q${question.order}`} color="primary" size="small" sx={{ fontWeight: 600, height: 22 }} />
          <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {question.question_text || 'Untitled'}
          </Typography>
          <Chip label={question.input_type} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
          {hasUnsavedChanges && <Chip label="•" size="small" color="warning" sx={{ height: 16, width: 16, '& .MuiChip-label': { p: 0 } }} />}
          {isDeveloper && (
            <Tooltip title="Delete Question">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => { e.stopPropagation(); onDelete(question.order); }}
                sx={{ p: 0.25 }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField
            size="small"
            label="Question Text"
            value={question.question_text || ''}
            onChange={(e) => updateField('question_text', e.target.value)}
            disabled={!isDeveloper}
            sx={{ flex: 2 }}
          />
          <TextField
            size="small"
            label="Highlight (italic)"
            value={question.question_text_highlight || ''}
            onChange={(e) => updateField('question_text_highlight', e.target.value)}
            disabled={!isDeveloper}
            sx={{ flex: 1 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={question.input_type || 'checkbox'}
              label="Type"
              onChange={(e) => updateField('input_type', e.target.value)}
              disabled={!isDeveloper}
            >
              <MenuItem value="checkbox">Checkbox</MenuItem>
              <MenuItem value="radio">Radio</MenuItem>
              <MenuItem value="text">Text</MenuItem>
              <MenuItem value="date">Date</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch size="small" checked={question.is_required ?? true} onChange={(e) => updateField('is_required', e.target.checked)} disabled={!isDeveloper} />}
            label={<Typography variant="body2">Required</Typography>}
            sx={{ ml: 0 }}
          />
          <TextField
            size="small"
            label="Salesforce Field"
            value={question.salesforce_field || ''}
            onChange={(e) => updateField('salesforce_field', e.target.value)}
            disabled={!isDeveloper}
            sx={{ flex: 1 }}
          />
        </Box>

        <Paper variant="outlined" sx={{ p: 1, mb: 1.5, bgcolor: '#fafafa' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            📹 Video & Overlay
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
            <Box sx={{ width: 120, flexShrink: 0 }}>
              {question.page_content?.media_url ? (
                <video
                  src={question.page_content.media_url}
                  poster={question.page_content.media_poster_url || undefined}
                  style={{ width: '100%', height: 68, objectFit: 'cover', borderRadius: 4 }}
                />
              ) : (
                <Box sx={{ width: '100%', height: 68, bgcolor: '#e0e0e0', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PlayIcon sx={{ opacity: 0.4 }} />
                </Box>
              )}
              <Button
                size="small"
                variant="text"
                startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
                onClick={() => onUploadVideo(question.order)}
                disabled={!isDeveloper || isUploading}
                sx={{ fontSize: 11, p: 0.25, mt: 0.5, width: '100%' }}
              >
                Video
              </Button>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="Video URL"
                  value={question.page_content?.media_url || ''}
                  disabled
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { fontSize: 12 } }}
                />
                {question.page_content?.media_url && (
                  <>
                    <Tooltip title="View version history">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const url = question.page_content?.media_url || '';
                          const path = url.replace('https://storage.googleapis.com/tripsby_public_hosted_content/', '');
                          onViewVersions(path, 'video');
                        }}
                      >
                        <HistoryIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove video">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onClearVideo(question.order)}
                        disabled={!isDeveloper}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <Tooltip title="Upload poster">
                  <IconButton size="small" onClick={() => onUploadPoster(question.order)} disabled={!isDeveloper || isUploading}>
                    <ImageIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <TextField
                  size="small"
                  label="Overlay Text"
                  value={question.page_content?.overlay?.text || ''}
                  onChange={(e) => updateOverlay('text', e.target.value)}
                  disabled={!isDeveloper}
                  sx={{ flex: 2 }}
                  InputProps={{ sx: { fontSize: 12 } }}
                />
                <TextField
                  size="small"
                  label="Attribution"
                  value={question.page_content?.overlay?.attribution || ''}
                  onChange={(e) => updateOverlay('attribution', e.target.value)}
                  disabled={!isDeveloper}
                  sx={{ flex: 1 }}
                  InputProps={{ sx: { fontSize: 12 } }}
                  placeholder="@user"
                />
                <Tooltip title="Upload avatar">
                  <IconButton size="small" onClick={() => onUploadAvatar(question.order)} disabled={!isDeveloper || isUploading}>
                    <UploadIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Paper>

        {['checkbox', 'radio'].includes(question.input_type || '') && (
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.5, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Options ({question.options?.length || 0})
              </Typography>
              <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={addOption} disabled={!isDeveloper} sx={{ fontSize: 11, py: 0 }}>
                Add
              </Button>
            </Box>

            {question.options && question.options.length > 0 ? (
              <Table size="small" sx={{ '& td, & th': { py: 0.25, px: 0.5 } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fafafa' }}>
                    <TableCell sx={{ width: 32, p: 0.5 }} />
                    <TableCell sx={{ fontSize: 11, fontWeight: 600, width: '15%' }}>ID</TableCell>
                    <TableCell sx={{ fontSize: 11, fontWeight: 600, width: '30%' }}>UI Label</TableCell>
                    <TableCell sx={{ fontSize: 11, fontWeight: 600, width: '20%' }}>Frontend Val</TableCell>
                    <TableCell sx={{ fontSize: 11, fontWeight: 600, width: '25%' }}>Salesforce Val</TableCell>
                    <TableCell sx={{ width: 32, p: 0.5 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={question.options.map((o, i) => o.id || `opt-${i}`)} strategy={verticalListSortingStrategy}>
                      {question.options.map((opt, idx) => (
                        <SortableOption
                          key={opt.id || `opt-${idx}`}
                          option={opt}
                          index={idx}
                          onUpdate={updateOption}
                          onRemove={removeOption}
                          onAutoGen={autoGenFirebaseValue}
                          disabled={!isDeveloper}
                          isNewSfValue={isNewSfValue(opt.salesforce_value || '')}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1.5, textAlign: 'center' }}>
                No options. Click "Add" to create one.
              </Typography>
            )}
          </Paper>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// =============================================================================
// History Drawer
// =============================================================================

interface HistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  history: OnboardingHistoryEntry[];
  currentVersion: string;
  onRestore: (versionId: string) => void;
  isRestoring: boolean;
}

function HistoryDrawer({ open, onClose, history, currentVersion, onRestore, isRestoring }: HistoryDrawerProps) {
  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 320 } }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Version History</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Current: v{currentVersion}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {history.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No history available.</Typography>
        ) : (
          <List dense disablePadding>
            {history.map((entry) => (
              <ListItem key={entry.id} divider sx={{ px: 0 }}>
                <ListItemText
                  primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>v{entry.version}</Typography>}
                  secondary={
                    <Box>
                      <Typography variant="caption" display="block">{formatDate(entry.archived_at)}</Typography>
                      <Typography variant="caption" color="text.secondary">{entry.archived_by?.name || entry.archived_by?.email}</Typography>
                      {entry.change_description && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                          {entry.change_description}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Restore this version">
                    <IconButton size="small" onClick={() => onRestore(entry.id)} disabled={isRestoring}>
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Drawer>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export default function OnboardingSurvey() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isDeveloper = user?.role === 'developer' || user?.role === 'admin';

  // State
  const [localConfig, setLocalConfig] = useState<OnboardingSurveyConfig | null>(null);
  const [originalConfig, setOriginalConfig] = useState<OnboardingSurveyConfig | null>(null);
  const [undoStack, setUndoStack] = useState<UndoState[]>([]);
  const [redoStack, setRedoStack] = useState<UndoState[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [changeDescription, setChangeDescription] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [isUploading, setIsUploading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [_selectedAssetPath, setSelectedAssetPath] = useState<string>('');
  const [selectedAssetType, setSelectedAssetType] = useState<'video' | 'poster' | 'avatar'>('video');
  const [assetVersions, setAssetVersions] = useState<AssetVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(false);

  // Copy from Staging state (Production only)
  const [stagingDialogOpen, setStagingDialogOpen] = useState(false);
  const [stagingFiles, setStagingFiles] = useState<StagingFile[]>([]);
  const [loadingStagingPreview, setLoadingStagingPreview] = useState(false);
  const [copyingFromStaging, setCopyingFromStaging] = useState(false);
  const [copyResult, setCopyResult] = useState<{ copied: number; skipped: number; failed: number } | null>(null);

  const knownSfValues = useMemo(() => collectKnownSfValues(originalConfig), [originalConfig]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadContextRef = useRef<{ order: number; type: 'video' | 'poster' | 'avatar'; target?: 'question' | 'start_screen' | 'end_screen' } | null>(null);

  // Queries
  const { data: config, isLoading, error } = useQuery('onboardingConfig', () => api.getOnboardingConfig());
  const { data: historyData } = useQuery('onboardingHistory', () => api.getOnboardingHistory());

  const saveMutation = useMutation(
    (data: { config: OnboardingSurveyConfig; changeDescription: string }) =>
      api.updateOnboardingConfig(data.config, data.changeDescription),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('onboardingConfig');
        queryClient.invalidateQueries('onboardingHistory');
        setSnackbar({ open: true, message: 'Configuration saved!', severity: 'success' });
        setSaveDialogOpen(false);
        setChangeDescription('');
        setOriginalConfig(localConfig);
        setUndoStack([]);
        setRedoStack([]);
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Failed to save configuration', severity: 'error' });
      },
    }
  );

  const restoreMutation = useMutation(
    (versionId: string) => api.restoreOnboardingVersion(versionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('onboardingConfig');
        queryClient.invalidateQueries('onboardingHistory');
        setSnackbar({ open: true, message: 'Version restored!', severity: 'success' });
        setHistoryOpen(false);
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Failed to restore version', severity: 'error' });
      },
    }
  );

  // Sync config to local state
  useEffect(() => {
    if (config) {
      setLocalConfig(config);
      setOriginalConfig(config);
      if (config.questions.length > 0 && !expandedId) {
        setExpandedId(config.questions[0].id || `q-${config.questions[0].order}`);
      }
    }
  }, [config]);

  const hasUnsavedChanges = useMemo(() => {
    if (!localConfig || !originalConfig) return false;
    return JSON.stringify(localConfig) !== JSON.stringify(originalConfig);
  }, [localConfig, originalConfig]);

  const getQuestionUnsavedStatus = useCallback(
    (order: number) => {
      if (!localConfig || !originalConfig) return false;
      const localQ = localConfig.questions.find((q) => q.order === order);
      const originalQ = originalConfig.questions.find((q) => q.order === order);
      return JSON.stringify(localQ) !== JSON.stringify(originalQ);
    },
    [localConfig, originalConfig]
  );

  const pushUndo = useCallback(() => {
    if (localConfig) {
      setUndoStack((prev) => [...prev.slice(-19), { config: localConfig, timestamp: Date.now() }]);
      setRedoStack([]);
    }
  }, [localConfig]);

  const handleUndo = () => {
    if (undoStack.length > 0) {
      const prev = undoStack[undoStack.length - 1];
      if (localConfig) setRedoStack((r) => [...r, { config: localConfig, timestamp: Date.now() }]);
      setLocalConfig(prev.config);
      setUndoStack((s) => s.slice(0, -1));
    }
  };

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const next = redoStack[redoStack.length - 1];
      if (localConfig) setUndoStack((u) => [...u, { config: localConfig, timestamp: Date.now() }]);
      setLocalConfig(next.config);
      setRedoStack((r) => r.slice(0, -1));
    }
  };

  const handleQuestionChange = useCallback(
    (updated: OnboardingQuestion) => {
      pushUndo();
      setLocalConfig((prev) => {
        if (!prev) return prev;
        const questions = prev.questions.map((q) => (q.order === updated.order ? updated : q));
        return { ...prev, questions };
      });
    },
    [pushUndo]
  );

  const handleAddQuestion = useCallback(() => {
    pushUndo();
    setLocalConfig((prev) => {
      if (!prev) return prev;
      const maxOrder = Math.max(...prev.questions.map((q) => q.order), 0);
      const newQuestion: OnboardingQuestion = {
        id: `q${maxOrder + 1}_new_${Date.now()}`,
        order: maxOrder + 1,
        question_text: 'New Question',
        question_text_highlight: null,
        input_type: 'checkbox',
        is_required: true,
        salesforce_field: '',
        page_content: {
          media_type: 'video',
          media_url: null,
          media_poster_url: null,
          overlay: {
            text: '',
            attribution: '',
            avatar_url: null,
          },
        },
        options: [],
      };
      const newQId = newQuestion.id || `q-${newQuestion.order}`;
      setExpandedId(newQId);
      return { ...prev, questions: [...prev.questions, newQuestion] };
    });
  }, [pushUndo]);

  const handleDeleteQuestion = useCallback(
    (order: number) => {
      pushUndo();
      setLocalConfig((prev) => {
        if (!prev) return prev;
        const questions = prev.questions
          .filter((q) => q.order !== order)
          .map((q, idx) => ({ ...q, order: idx + 1 })); // Re-number orders
        return { ...prev, questions };
      });
    },
    [pushUndo]
  );

  const handleUploadClick = (order: number, type: 'video' | 'poster' | 'avatar', target: 'question' | 'start_screen' | 'end_screen' = 'question') => {
    uploadContextRef.current = { order, type, target };
    if (fileInputRef.current) {
      fileInputRef.current.accept = type === 'video' ? 'video/mp4,video/webm' : 'image/jpeg,image/png,image/webp';
      fileInputRef.current.click();
    }
  };

  const handleViewVersions = async (assetPath: string, assetType: 'video' | 'poster' | 'avatar') => {
    setSelectedAssetPath(assetPath);
    setSelectedAssetType(assetType);
    setVersionDialogOpen(true);
    setLoadingVersions(true);
    try {
      const result = await api.listAssetVersions(assetPath, assetType);
      setAssetVersions(result.versions);
    } catch (err) {
      console.error('Failed to load versions:', err);
      setSnackbar({ open: true, message: 'Failed to load version history', severity: 'error' });
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRestoreVersion = async (version: AssetVersion) => {
    if (!window.confirm(`Restore "${version.filename}" from ${new Date(version.created_at).toLocaleString()}?\n\nThe current version will be backed up automatically.`)) {
      return;
    }
    setRestoringVersion(true);
    try {
      const result = await api.restoreAssetVersion(version.id);
      setSnackbar({ open: true, message: 'Version restored successfully!', severity: 'success' });
      setVersionDialogOpen(false);

      // Update the local config with the restored URL
      pushUndo();
      setLocalConfig((prev) => {
        if (!prev) return prev;
        const questions = prev.questions.map((q) => {
          const currentUrl = q.page_content?.media_url || '';
          const currentPath = currentUrl.replace('https://storage.googleapis.com/tripsby_public_hosted_content/', '');
          if (currentPath === version.original_path) {
            if (version.asset_type === 'video') {
              return { ...q, page_content: { ...q.page_content, media_url: result.public_url + '?v=' + Date.now() } };
            } else if (version.asset_type === 'poster') {
              return { ...q, page_content: { ...q.page_content, media_poster_url: result.public_url + '?v=' + Date.now() } };
            }
          }
          return q;
        });
        return { ...prev, questions };
      });
    } catch (err: any) {
      console.error('Failed to restore version:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to restore version', severity: 'error' });
    } finally {
      setRestoringVersion(false);
    }
  };

  const handleDeleteVersion = async (version: AssetVersion) => {
    if (!window.confirm(`Delete this version from ${new Date(version.created_at).toLocaleString()}?\n\nThis cannot be undone.`)) {
      return;
    }
    try {
      await api.deleteAssetVersion(version.id);
      setAssetVersions((prev) => prev.filter((v) => v.id !== version.id));
      setSnackbar({ open: true, message: 'Version deleted', severity: 'success' });
    } catch (err: any) {
      console.error('Failed to delete version:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to delete version', severity: 'error' });
    }
  };

  // Copy from Staging handlers (Production only)
  const handleOpenStagingDialog = async () => {
    setStagingDialogOpen(true);
    setLoadingStagingPreview(true);
    setCopyResult(null);
    try {
      const result = await api.getStagingPreview();
      setStagingFiles(result.files);
    } catch (err: any) {
      console.error('Failed to load staging preview:', err);
      if (err.message?.includes('only available in production')) {
        setSnackbar({ open: true, message: 'Copy from Staging is only available in production environment', severity: 'error' });
        setStagingDialogOpen(false);
      } else {
        setSnackbar({ open: true, message: err.message || 'Failed to load staging preview', severity: 'error' });
      }
    } finally {
      setLoadingStagingPreview(false);
    }
  };

  const handleCopyFromStaging = async () => {
    if (!window.confirm('Copy all onboarding assets from staging to production?\n\nExisting files in production that are older than staging will be overwritten.')) {
      return;
    }
    setCopyingFromStaging(true);
    try {
      const result = await api.copyFromStaging();
      setCopyResult({
        copied: result.summary.copied_count,
        skipped: result.summary.skipped_count,
        failed: result.summary.failed_count,
      });
      setSnackbar({
        open: true,
        message: `Copy complete: ${result.summary.copied_count} copied, ${result.summary.skipped_count} skipped, ${result.summary.failed_count} failed`,
        severity: result.summary.failed_count > 0 ? 'error' : 'success'
      });
      // Refresh the onboarding config to pick up new URLs
      queryClient.invalidateQueries('onboardingConfig');
    } catch (err: any) {
      console.error('Failed to copy from staging:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to copy from staging', severity: 'error' });
    } finally {
      setCopyingFromStaging(false);
    }
  };

  const handleClearVideo = (order: number) => {
    if (!window.confirm('Remove the video from this question?\n\nThe video file will remain in storage but will no longer be associated with this question.')) {
      return;
    }
    pushUndo();
    setLocalConfig((prev) => {
      if (!prev) return prev;
      const questions = prev.questions.map((q) => {
        if (q.order !== order) return q;
        return {
          ...q,
          page_content: {
            ...q.page_content,
            media_url: '',
            media_poster_url: '',
          },
        };
      });
      return { ...prev, questions };
    });
    setSnackbar({ open: true, message: 'Video removed. Remember to save your changes.', severity: 'success' });
  };

  const handleScreenChange = (screenType: 'start_screen' | 'end_screen', screen: OnboardingScreen) => {
    pushUndo();
    setLocalConfig((prev) => {
      if (!prev) return prev;
      return { ...prev, [screenType]: screen };
    });
  };

  const handleClearScreenVideo = (screenType: 'start_screen' | 'end_screen') => {
    if (!window.confirm('Remove the video from this screen?')) {
      return;
    }
    pushUndo();
    setLocalConfig((prev) => {
      if (!prev) return prev;
      const currentScreen = prev[screenType];
      if (!currentScreen) return prev;
      return {
        ...prev,
        [screenType]: {
          ...currentScreen,
          page_content: {
            ...currentScreen.page_content,
            media_url: '',
            media_poster_url: '',
          },
        },
      };
    });
    setSnackbar({ open: true, message: 'Video removed. Remember to save your changes.', severity: 'success' });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const context = uploadContextRef.current;
    if (!file || !context || !localConfig) return;

    setIsUploading(true);
    try {
      const { order, type, target = 'question' } = context;
      const ext = file.name.split('.').pop() || (type === 'video' ? 'mp4' : 'jpg');

      // Determine filename based on target
      let filename: string;
      const typeSuffix = type === 'poster' ? '-poster' : type === 'avatar' ? '-avatar' : '';
      if (target === 'start_screen') {
        filename = `start-screen${typeSuffix}.${ext}`;
      } else if (target === 'end_screen') {
        filename = `end-screen${typeSuffix}.${ext}`;
      } else {
        filename = `step${order}${typeSuffix}.${ext}`;
      }

      const uploadInfo = await api.getOnboardingUploadUrl(filename, file.type, type);

      const uploadResponse = await fetch(uploadInfo.upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      pushUndo();

      if (target === 'start_screen' || target === 'end_screen') {
        // Handle screen uploads
        setLocalConfig((prev) => {
          if (!prev) return prev;
          const screenKey = target as 'start_screen' | 'end_screen';
          const currentScreen = prev[screenKey] || {
            enabled: true,
            title: '',
            subtitle: '',
            button_text: 'Continue',
            page_content: { media_type: 'video', media_url: null, media_poster_url: null, overlay: { text: '', attribution: '', avatar_url: '' } },
          };

          if (type === 'video') {
            return {
              ...prev,
              [screenKey]: {
                ...currentScreen,
                page_content: { ...currentScreen.page_content!, media_url: uploadInfo.public_url },
              },
            };
          } else if (type === 'poster') {
            return {
              ...prev,
              [screenKey]: {
                ...currentScreen,
                page_content: { ...currentScreen.page_content!, media_poster_url: uploadInfo.public_url },
              },
            };
          } else if (type === 'avatar') {
            return {
              ...prev,
              [screenKey]: {
                ...currentScreen,
                page_content: {
                  ...currentScreen.page_content!,
                  overlay: { ...currentScreen.page_content?.overlay, avatar_url: uploadInfo.public_url },
                },
              },
            };
          }
          return prev;
        });
      } else {
        // Handle question uploads
        setLocalConfig((prev) => {
          if (!prev) return prev;
          const questions = prev.questions.map((q) => {
            if (q.order !== order) return q;
            if (type === 'video') {
              return { ...q, page_content: { ...q.page_content, media_url: uploadInfo.public_url } };
            } else if (type === 'poster') {
              return { ...q, page_content: { ...q.page_content, media_poster_url: uploadInfo.public_url, poster_source: 'custom' as const } };
            } else if (type === 'avatar') {
              return { ...q, page_content: { ...q.page_content, overlay: { ...q.page_content?.overlay, avatar_url: uploadInfo.public_url } } };
            }
            return q;
          });
          return { ...prev, questions };
        });
      }

      if (type === 'video') {
        try {
          const posterBlob = await extractFirstFrame(uploadInfo.public_url);
          const posterFilename = `step${order}-poster.jpg`;
          const posterUploadInfo = await api.getOnboardingUploadUrl(posterFilename, 'image/jpeg', 'poster');
          await fetch(posterUploadInfo.upload_url, {
            method: 'PUT',
            body: posterBlob,
            headers: { 'Content-Type': 'image/jpeg' },
          });

          setLocalConfig((prev) => {
            if (!prev) return prev;
            const questions = prev.questions.map((q) => {
              if (q.order !== order) return q;
              if (!q.page_content?.poster_source || q.page_content.poster_source === 'auto') {
                return { ...q, page_content: { ...q.page_content, media_poster_url: posterUploadInfo.public_url, poster_source: 'auto' as const } };
              }
              return q;
            });
            return { ...prev, questions };
          });
        } catch (err) {
          console.warn('Auto-poster generation failed:', err);
        }
      }

      setSnackbar({ open: true, message: 'Upload successful!', severity: 'success' });
    } catch (err) {
      console.error('Upload failed:', err);
      setSnackbar({ open: true, message: 'Upload failed', severity: 'error' });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (error || !localConfig) return <Box sx={{ p: 3 }}><Alert severity="error">Failed to load config.</Alert></Box>;

  const sortedQuestions = [...localConfig.questions].sort((a, b) => a.order - b.order);
  const activeQuestion = sortedQuestions.find(q => (q.id || `q-${q.order}`) === expandedId) || sortedQuestions[0];
  const activeIndex = sortedQuestions.indexOf(activeQuestion);

  return (
    <Box sx={{ p: 2, maxWidth: 1600, mx: 'auto' }}>
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }}>BY.AI Group<br/>Command Center</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={`v${localConfig.version}`} size="small" color="primary" variant="outlined" />
          {hasUnsavedChanges && <Chip label="Unsaved changes" size="small" color="warning" />}
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Tooltip title="Undo"><IconButton onClick={handleUndo} disabled={undoStack.length === 0}><UndoIcon /></IconButton></Tooltip>
          <Tooltip title="Redo"><IconButton onClick={handleRedo} disabled={redoStack.length === 0}><RedoIcon /></IconButton></Tooltip>
          <Tooltip title="History"><IconButton onClick={() => setHistoryOpen(true)}><HistoryIcon /></IconButton></Tooltip>
          {user?.role === 'admin' && (
            <Tooltip title="Copy assets from staging environment">
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CloudSyncIcon />}
                onClick={handleOpenStagingDialog}
                size="small"
                sx={{ ml: 1 }}
              >
                Copy from Staging
              </Button>
            </Tooltip>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => setSaveDialogOpen(true)}
            disabled={!hasUnsavedChanges || !isDeveloper}
            size="small"
            sx={{ ml: 1 }}
          >
            Save
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left: Questions List */}
        <Grid item xs={12} md={7} lg={8}>
          {/* Start Screen */}
          <ScreenEditor
            title="🎬 Start Screen (Intro/Welcome)"
            screen={localConfig?.start_screen}
            onChange={(screen) => handleScreenChange('start_screen', screen)}
            onUploadVideo={() => handleUploadClick(0, 'video', 'start_screen')}
            onUploadPoster={() => handleUploadClick(0, 'poster', 'start_screen')}
            onUploadAvatar={() => handleUploadClick(0, 'avatar', 'start_screen')}
            onViewVersions={(path) => handleViewVersions(path, 'video')}
            onClearVideo={() => handleClearScreenVideo('start_screen')}
            isUploading={isUploading}
            isDeveloper={isDeveloper}
          />

          <Card>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                📋 Survey Questions ({sortedQuestions.length})
              </Typography>
              {sortedQuestions.map((question) => {
                const qId = question.id || `q-${question.order}`;
                return (
                  <QuestionEditor
                    key={question.order}
                    question={question}
                    onChange={handleQuestionChange}
                    onDelete={handleDeleteQuestion}
                    onUploadVideo={(o) => handleUploadClick(o, 'video')}
                    onUploadPoster={(o) => handleUploadClick(o, 'poster')}
                    onUploadAvatar={(o) => handleUploadClick(o, 'avatar')}
                    onViewVersions={handleViewVersions}
                    onClearVideo={handleClearVideo}
                    isUploading={isUploading}
                    isDeveloper={isDeveloper}
                    hasUnsavedChanges={getQuestionUnsavedStatus(question.order)}
                    knownSfValues={knownSfValues}
                    expanded={expandedId === qId}
                    onExpand={(isExp) => {
                      if (isExp) setExpandedId(qId);
                      else if (expandedId === qId) setExpandedId(null);
                    }}
                  />
                );
              })}

              {/* Add Question Button */}
              {isDeveloper && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddQuestion}
                  sx={{ mt: 1, borderStyle: 'dashed' }}
                >
                  Add New Question
                </Button>
              )}
            </CardContent>
          </Card>

          {/* End Screen */}
          <ScreenEditor
            title="🎉 End Screen (Completion/Thank You)"
            screen={localConfig?.end_screen}
            onChange={(screen) => handleScreenChange('end_screen', screen)}
            onUploadVideo={() => handleUploadClick(0, 'video', 'end_screen')}
            onUploadPoster={() => handleUploadClick(0, 'poster', 'end_screen')}
            onUploadAvatar={() => handleUploadClick(0, 'avatar', 'end_screen')}
            onViewVersions={(path) => handleViewVersions(path, 'video')}
            onClearVideo={() => handleClearScreenVideo('end_screen')}
            isUploading={isUploading}
            isDeveloper={isDeveloper}
          />
        </Grid>

        {/* Right: Sticky Preview */}
        <Grid item xs={12} md={5} lg={4}>
          <Box sx={{ position: 'sticky', top: 24 }}>
            {activeQuestion && (
              <SurveyPreview
                question={activeQuestion}
                currentStep={activeIndex + 1}
                totalSteps={sortedQuestions.length}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Changes</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus margin="dense" label="Change Description" fullWidth multiline rows={2}
            value={changeDescription} onChange={(e) => setChangeDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => saveMutation.mutate({ config: localConfig!, changeDescription })} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <HistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={historyData || []}
        currentVersion={localConfig?.version || '0.0'}
        onRestore={(id) => restoreMutation.mutate(id)}
        isRestoring={restoreMutation.isLoading}
      />

      {/* Asset Version History Dialog */}
      <Dialog open={versionDialogOpen} onClose={() => setVersionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon />
          Version History - {selectedAssetType.charAt(0).toUpperCase() + selectedAssetType.slice(1)}
        </DialogTitle>
        <DialogContent>
          {loadingVersions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : assetVersions.length === 0 ? (
            <Alert severity="info">No previous versions found for this asset.</Alert>
          ) : (
            <List>
              {assetVersions.map((version) => (
                <ListItem
                  key={version.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: version.is_pre_restore_backup ? 'action.hover' : 'background.paper',
                  }}
                >
                  {selectedAssetType === 'video' ? (
                    <Box sx={{ width: 120, height: 68, mr: 2, flexShrink: 0, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PlayIcon sx={{ fontSize: 32, color: 'grey.500' }} />
                    </Box>
                  ) : (
                    <Box
                      component="img"
                      src={version.public_url}
                      sx={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 1, mr: 2 }}
                    />
                  )}
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">{version.filename}</Typography>
                        {version.is_pre_restore_backup && (
                          <Chip label="Pre-restore backup" size="small" color="warning" variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="caption" display="block">
                          {new Date(version.created_at).toLocaleString()} by {version.created_by}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(version.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Preview">
                      <IconButton
                        size="small"
                        onClick={() => window.open(version.public_url, '_blank')}
                        sx={{ mr: 1 }}
                      >
                        <PlayIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RestoreIcon />}
                      onClick={() => handleRestoreVersion(version)}
                      disabled={restoringVersion}
                      sx={{ mr: 1 }}
                    >
                      Restore
                    </Button>
                    {user?.role === 'admin' && (
                      <Tooltip title="Delete this version">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteVersion(version)}
                          disabled={restoringVersion}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Copy from Staging Dialog (Production only) */}
      <Dialog open={stagingDialogOpen} onClose={() => setStagingDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudSyncIcon color="secondary" />
          Copy Onboarding Assets from Staging
        </DialogTitle>
        <DialogContent>
          {copyResult && (
            <Alert
              severity={copyResult.failed > 0 ? 'warning' : 'success'}
              sx={{ mb: 2 }}
              icon={copyResult.failed > 0 ? <ErrorIcon /> : <CheckCircleIcon />}
            >
              <Typography variant="body2">
                <strong>Copy Complete:</strong> {copyResult.copied} copied, {copyResult.skipped} skipped
                {copyResult.failed > 0 && <>, <span style={{ color: 'red' }}>{copyResult.failed} failed</span></>}
              </Typography>
            </Alert>
          )}

          {loadingStagingPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : stagingFiles.length === 0 ? (
            <Alert severity="info">No files found in staging bucket.</Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Found <strong>{stagingFiles.length}</strong> files in staging. Files that are newer than production will be copied.
                </Typography>
              </Alert>
              <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                {stagingFiles.map((file) => (
                  <ListItem
                    key={file.path}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 0.5,
                      py: 0.5,
                    }}
                  >
                    {file.content_type?.startsWith('video/') ? (
                      <Box sx={{ width: 60, height: 40, mr: 1.5, flexShrink: 0, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PlayIcon sx={{ fontSize: 20, color: 'grey.500' }} />
                      </Box>
                    ) : (
                      <Box
                        component="img"
                        src={file.public_url}
                        sx={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 1, mr: 1.5, flexShrink: 0 }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {file.path.split('/').pop()}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {file.path} • {(file.size / 1024).toFixed(1)} KB
                          {file.updated && ` • ${new Date(file.updated).toLocaleDateString()}`}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStagingDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCopyFromStaging}
            disabled={loadingStagingPreview || copyingFromStaging || stagingFiles.length === 0}
            startIcon={copyingFromStaging ? <CircularProgress size={16} color="inherit" /> : <CloudSyncIcon />}
          >
            {copyingFromStaging ? 'Copying...' : 'Copy All to Production'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
